import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Send, Users, Tag } from 'lucide-react';
import { toast } from 'sonner';

interface NotificationTag {
  id: string;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
}

const NotificationSend: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [tags, setTags] = useState<NotificationTag[]>([]);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    type: 'broadcast',
    title: '',
    message: '',
    tags: [] as string[],
    flex_message: '',
  });

  useEffect(() => {
    fetchTags();
  }, [tenantStore.selectedTenant?.id]);

  const fetchTags = async () => {
    if (!tenantStore.selectedTenant?.id) return;
    
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notification-tags`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTags(data.filter((tag: NotificationTag) => tag.is_active) || []);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleTagToggle = (tagId: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((id) => id !== tagId)
        : [...prev.tags, tagId],
    }));
  };

  const handleSend = async () => {
    if (!tenantStore.selectedTenant?.id) return;
    
    if (!formData.title || !formData.message) {
      toast.error('タイトルとメッセージは必須です');
      return;
    }
    
    if (formData.type === 'targeted' && formData.tags.length === 0) {
      toast.error('ターゲット配信の場合はタグを選択してください');
      return;
    }
    
    setSending(true);
    
    try {
      const payload: any = {
        type: formData.type,
        title: formData.title,
        message: formData.message,
      };
      
      if (formData.type === 'targeted') {
        payload.tags = formData.tags;
      }
      
      if (formData.flex_message) {
        try {
          JSON.parse(formData.flex_message);
          payload.flex_message = formData.flex_message;
        } catch {
          toast.error('Flex Messageの形式が正しくありません');
          setSending(false);
          return;
        }
      }
      
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notifications/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(
          `通知を送信しました (対象: ${result.target_count}件)`
        );
        
        // フォームをリセット
        setFormData({
          type: 'broadcast',
          title: '',
          message: '',
          tags: [],
          flex_message: '',
        });
      } else {
        const error = await response.json();
        toast.error(error.error || '送信に失敗しました');
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      toast.error('送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            プッシュ通知送信
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label>配信タイプ</Label>
              <RadioGroup
                value={formData.type}
                onValueChange={(value) =>
                  setFormData({ ...formData, type: value })
                }
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="broadcast" id="broadcast" />
                  <Label htmlFor="broadcast" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      全員に配信
                    </div>
                    <div className="text-sm text-gray-600">
                      通知を有効にしている全てのユーザーに配信します
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="targeted" id="targeted" />
                  <Label htmlFor="targeted" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      タグで絞り込み
                    </div>
                    <div className="text-sm text-gray-600">
                      選択したタグを設定しているユーザーに配信します
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {formData.type === 'targeted' && (
              <div>
                <Label>配信対象タグ</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant={
                        formData.tags.includes(tag.id) ? 'default' : 'outline'
                      }
                      className="cursor-pointer"
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      <div
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </Badge>
                  ))}
                </div>
                {tags.length === 0 && (
                  <div className="text-sm text-gray-500 mt-2">
                    有効なタグがありません
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="title">タイトル</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="お知らせ"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="message">メッセージ</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                placeholder="配信するメッセージを入力してください"
                rows={5}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="flex_message">
                Flex Message (オプション)
              </Label>
              <Textarea
                id="flex_message"
                value={formData.flex_message}
                onChange={(e) =>
                  setFormData({ ...formData, flex_message: e.target.value })
                }
                placeholder="LINE Flex Message JSON (省略可)"
                rows={5}
                className="mt-2 font-mono text-sm"
              />
              <div className="text-sm text-gray-600 mt-1">
                <a
                  href="https://developers.line.biz/flex-simulator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Flex Message Simulator
                </a>
                で作成したJSONを貼り付けてください
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSend}
              disabled={sending || !formData.title || !formData.message}
              className="min-w-32"
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  送信中...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  送信
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default NotificationSend;